import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, Eye, ImagePlus, MessageCircle, Pencil, Send, Trash2, X } from "lucide-react";
import DeleteConfirmation from "../components/DeleteConfirmation";
import MemberGate from "../components/MemberGate";
import { ErrorMessage, Loading } from "../components/Loading";
import { useApi } from "../hooks/useApi";
import { api } from "../lib/api";
import { reactionTotal, storyDate, storyReactionOptions } from "../lib/stories";

function memberLabel(member) {
  return `${member.firstName || ""} ${member.lastName || ""}`
    .replace(/<[^>]*>/gu, " ")
    .replace(/\s+/gu, " ")
    .trim();
}

function StoryEditor({ story, onClose, onSaved }) {
  const [title, setTitle] = useState(story.title);
  const [body, setBody] = useState(story.body);
  const [authorMemberId, setAuthorMemberId] = useState(String(story.author.memberId));
  const [members, setMembers] = useState([]);
  const [cover, setCover] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [membersLoading, setMembersLoading] = useState(Boolean(story.canChangeAuthor));
  const dialog = useRef(null);
  const localPreview = useMemo(() => cover ? URL.createObjectURL(cover) : "", [cover]);

  useEffect(() => {
    dialog.current?.focus();
  }, []);

  useEffect(() => () => {
    if (localPreview) URL.revokeObjectURL(localPreview);
  }, [localPreview]);

  useEffect(() => {
    const closeOnEscape = (event) => event.key === "Escape" && !saving && onClose();
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose, saving]);

  useEffect(() => {
    if (!story.canChangeAuthor) return undefined;
    let cancelled = false;
    api("/api/members")
      .then((result) => {
        if (!cancelled) setMembers(result.members || []);
      })
      .catch((requestError) => {
        if (!cancelled) setError(requestError.message);
      })
      .finally(() => {
        if (!cancelled) setMembersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [story.canChangeAuthor]);

  async function save(event) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const form = new FormData();
      form.set("title", title);
      form.set("body", body);
      if (story.canChangeAuthor) form.set("authorMemberId", authorMemberId);
      if (cover) form.set("cover", cover);
      const result = await api(`/api/stories/${story.id}`, { method: "PATCH", body: form });
      onSaved(result.story);
      onClose();
    } catch (requestError) {
      setError(requestError.message);
      setSaving(false);
    }
  }

  const hasCurrentAuthor = members.some((member) => Number(member.id) === Number(story.author.memberId));

  return (
    <div className="modal-backdrop story-composer-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && !saving && onClose()}>
      <section className="story-composer story-editor" role="dialog" aria-modal="true" aria-labelledby="story-editor-title" tabIndex="-1" ref={dialog}>
        <header className="story-composer__header">
          <div>
            <p className="kicker">Story editor</p>
            <h2 id="story-editor-title">Refine the memory.</h2>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Close story editor" disabled={saving}><X aria-hidden="true" /></button>
        </header>
        <form className="story-composer__form" onSubmit={save}>
          <div className="story-composer__fields">
            {story.canChangeAuthor && (
              <label className="form-field">
                Story author
                <select required value={authorMemberId} onChange={(event) => setAuthorMemberId(event.target.value)} disabled={membersLoading || saving}>
                  {!hasCurrentAuthor && <option value={story.author.memberId}>{story.author.name}</option>}
                  {members.map((member) => <option value={member.id} key={member.id}>{memberLabel(member)}{member.branch ? ` · ${member.branch}` : ""}</option>)}
                </select>
                <small>{membersLoading ? "Loading class profiles…" : "Changing the author also changes who can edit this story."}</small>
              </label>
            )}
            <label className="form-field">
              Story title
              <input required minLength="3" maxLength="140" value={title} onChange={(event) => setTitle(event.target.value)} disabled={saving} />
              <small>{title.length} / 140</small>
            </label>
            <label className="form-field">
              Story
              <textarea required minLength="20" maxLength="20000" rows="14" value={body} onChange={(event) => setBody(event.target.value)} disabled={saving} />
              <small>{body.length.toLocaleString()} / 20,000</small>
            </label>
          </div>
          <label className="story-cover-picker has-preview">
            <img src={localPreview || story.coverUrl} alt={cover ? "Replacement story cover preview" : `Current cover for ${story.title}`} />
            <span>{cover ? "Replacement cover selected" : "Choose a replacement cover"}</span>
            <small>{cover ? cover.name : "Keep the current picture, or choose JPEG, PNG, or WebP up to 8 MB"}</small>
            <input className="sr-only" type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => setCover(event.target.files?.[0] || null)} disabled={saving} />
          </label>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          <footer className="story-composer__actions">
            <button className="text-link" type="button" onClick={onClose} disabled={saving}>Cancel</button>
            <button className="button button--coral" disabled={saving || membersLoading} type="submit">{saving ? "Saving…" : "Save story"} <Check aria-hidden="true" /></button>
          </footer>
        </form>
      </section>
    </div>
  );
}

function ReactionBar({ story, canContribute, onReact, working }) {
  return (
    <div className="story-reactions">
      <div className="story-reactions__summary">
        <strong>{reactionTotal(story.reactionCounts)}</strong>
        <span>{reactionTotal(story.reactionCounts) === 1 ? "reaction" : "reactions"}</span>
      </div>
      <div className="story-reactions__choices" aria-label="React to this story">
        {storyReactionOptions.map((option) => (
          <button
            className={story.myReaction === option.key ? "story-reaction is-active" : "story-reaction"}
            type="button"
            key={option.key}
            disabled={!canContribute || working}
            aria-pressed={story.myReaction === option.key}
            onClick={() => onReact(option.key)}
          >
            <span aria-hidden="true">{option.emoji}</span>
            <small>{option.label}</small>
            {Number(story.reactionCounts?.[option.key]) > 0 && <b>{story.reactionCounts[option.key]}</b>}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function StoryDetail({ session, onLogin }) {
  const { storyId } = useParams();
  const navigate = useNavigate();
  const authenticated = Boolean(session?.authenticated);
  const canContribute = Number(session?.user?.memberId) > 0;
  const [comment, setComment] = useState("");
  const [actionError, setActionError] = useState("");
  const [commenting, setCommenting] = useState(false);
  const [reacting, setReacting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const loader = useCallback(
    () => authenticated ? api(`/api/stories/${storyId}`) : Promise.resolve(null),
    [authenticated, storyId],
  );
  const { data, error, loading, setData } = useApi(loader, [loader]);
  const story = data?.story;

  async function react(reaction) {
    setReacting(true);
    setActionError("");
    try {
      const result = await api(`/api/stories/${storyId}/reactions`, { method: "POST", body: JSON.stringify({ reaction }) });
      setData((current) => ({ ...current, story: { ...current.story, ...result } }));
    } catch (requestError) {
      setActionError(requestError.message);
    } finally {
      setReacting(false);
    }
  }

  async function addComment(event) {
    event.preventDefault();
    setCommenting(true);
    setActionError("");
    try {
      const result = await api(`/api/stories/${storyId}/comments`, { method: "POST", body: JSON.stringify({ body: comment }) });
      setData((current) => ({
        ...current,
        story: { ...current.story, comments: [...current.story.comments, result.comment] },
      }));
      setComment("");
    } catch (requestError) {
      setActionError(requestError.message);
    } finally {
      setCommenting(false);
    }
  }

  async function deleteStory() {
    setDeleting(true);
    setDeleteError("");
    try {
      await api(`/api/stories/${storyId}`, { method: "DELETE" });
      navigate("/stories", { replace: true });
    } catch (requestError) {
      setDeleteError(requestError.message);
      setDeleting(false);
    }
  }

  if (!authenticated) {
    return <div className="stories-gate section-wrap"><MemberGate onSuccess={onLogin} /></div>;
  }
  if (loading) return <div className="story-detail-state section-wrap"><Loading label="Opening this story" /></div>;
  if (error || !story) return <div className="story-detail-state section-wrap"><ErrorMessage>{error || "Story not found."}</ErrorMessage><Link className="text-link" to="/stories"><ArrowLeft aria-hidden="true" /> Back to stories</Link></div>;

  return (
    <article className="story-detail">
      <header className="story-detail__header section-wrap">
        <div className="story-detail__toolbar">
          <Link className="back-link" to="/stories"><ArrowLeft aria-hidden="true" /> All class stories</Link>
          {(story.canEdit || story.canDelete) && (
            <div className="story-detail__actions">
              {story.canEdit && <button className="button button--ghost button--small" type="button" onClick={() => setEditing(true)}><Pencil aria-hidden="true" /> Edit story</button>}
              {story.canDelete && <button className="button button--danger button--small" type="button" onClick={() => { setDeleteError(""); setConfirmingDelete(true); }}><Trash2 aria-hidden="true" /> Delete story</button>}
            </div>
          )}
        </div>
        <p className="kicker">A memory by {story.author.name}</p>
        <h1>{story.title}</h1>
        <div className="story-detail__meta">
          <span>{story.author.branch || "MACE ’95"}</span>
          <span>{storyDate(story.createdAt)}</span>
          <span><Eye aria-hidden="true" /> {story.viewCount} {story.viewCount === 1 ? "read" : "reads"}</span>
          <span>{story.comments.length} {story.comments.length === 1 ? "comment" : "comments"}</span>
        </div>
      </header>
      <figure className="story-detail__cover">
        <img src={story.coverUrl} alt={`Cover for ${story.title}`} />
        <figcaption><span>From the MACE 95 living archive</span><span>Story / {story.author.name}</span></figcaption>
      </figure>
      <div className="story-detail__body section-wrap">
        <aside><span aria-hidden="true">“</span><p>Thirty years later, this is how I remember it.</p></aside>
        <p className="story-essay">{story.body}</p>
      </div>

      <section className="story-conversation">
        <div className="section-wrap">
          <ReactionBar story={story} canContribute={canContribute} onReact={react} working={reacting} />
          {!canContribute && <p className="story-account-note">You’re reading with shared class access. <Link to="/members">Sign in with your individual account</Link> to react or join the conversation.</p>}
          {actionError && <ErrorMessage>{actionError}</ErrorMessage>}
          <div className="story-comments">
            <header>
              <p className="kicker">The conversation</p>
              <h2>What this brings back</h2>
              <span><MessageCircle aria-hidden="true" /> {story.comments.length}</span>
            </header>
            {canContribute && (
              <form className="story-comment-form" onSubmit={addComment}>
                <label htmlFor="story-comment">Add your memory or response</label>
                <textarea id="story-comment" required maxLength="1200" rows="4" value={comment} onChange={(event) => setComment(event.target.value)} placeholder="What do you remember?" />
                <div><small>{comment.length} / 1,200</small><button className="button button--ink" disabled={commenting} type="submit">{commenting ? "Posting…" : "Post comment"} <Send aria-hidden="true" /></button></div>
              </form>
            )}
            <div className="story-comment-list">
              {story.comments.length === 0 && <p className="story-comments__empty">No comments yet. The first reply can be a memory of your own.</p>}
              {story.comments.map((item, index) => (
                <article className="story-comment" key={item.id}>
                  <span className="story-comment__index">{String(index + 1).padStart(2, "0")}</span>
                  <div>
                    <p>{item.body}</p>
                    <footer><strong>{item.author.name}</strong><span>{storyDate(item.createdAt)}</span></footer>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>
      {editing && <StoryEditor story={story} onClose={() => setEditing(false)} onSaved={(updatedStory) => setData((current) => ({ ...current, story: { ...current.story, ...updatedStory } }))} />}
      {confirmingDelete && (
        <DeleteConfirmation
          kicker="Administrator action"
          title={`Delete “${story.title}”?`}
          description="This permanently removes the story, its cover picture, comments, and reactions. This action cannot be undone."
          confirmLabel="Delete story"
          busy={deleting}
          error={deleteError}
          onCancel={() => setConfirmingDelete(false)}
          onConfirm={deleteStory}
        />
      )}
    </article>
  );
}
